import { getCurrentInstance, onBeforeUnmount, watch } from 'vue'
import { yXmlFragmentToProsemirrorJSON } from 'y-prosemirror'
import { countWordsFromContent } from '../utils/contentUtils'

export interface FolderOverviewResult {
  /** The page's opening lines. */
  snippet: string
  /** The page's last lines, where the writing stops; starts with "…" when cut. */
  endSnippet: string
  wordCount: number
}

const OVERVIEW_CACHE_TTL_MS = 60_000
const MAX_SNIPPET_CHARS = 220

const overviewCache = new Map<string, { result: FolderOverviewResult; fetchedAt: number }>()
const inFlight = new Map<string, Promise<FolderOverviewResult>>()

/** The text of each block in a stored ProseMirror document, in order. */
function blocksFromJson(parsed: any): string[] {
  const blocks: string[] = []
  function traverse(node: any) {
    if (!node || !Array.isArray(node.content)) return
    if (node.content.some((child: any) => typeof child?.text === 'string')) {
      const text = node.content.map((child: any) => child?.text ?? '').join('').trim()
      if (text) blocks.push(text)
      return
    }
    for (const child of node.content) traverse(child)
  }
  traverse(parsed)
  return blocks
}

function openingOf(blocks: string[]): string {
  let text = ''
  for (const block of blocks) {
    if (text.length >= MAX_SNIPPET_CHARS) break
    text += (text ? ' ' : '') + block
  }
  return text.slice(0, MAX_SNIPPET_CHARS)
}

/** The last lines of the page, cut at a word so the preview never starts mid-word. */
function endingOf(blocks: string[]): string {
  let text = ''
  for (let i = blocks.length - 1; i >= 0 && text.length < MAX_SNIPPET_CHARS; i--) {
    text = blocks[i] + (text ? ' ' : '') + text
  }
  if (text.length <= MAX_SNIPPET_CHARS) return text
  const tail = text.slice(-MAX_SNIPPET_CHARS)
  const firstSpace = tail.indexOf(' ')
  return `…${firstSpace >= 0 ? tail.slice(firstSpace + 1) : tail}`
}

function extractBlocks(content: string): string[] {
  if (!content) return []
  try {
    return blocksFromJson(JSON.parse(content))
  } catch {
    return []
  }
}

function overviewOf(content: string): FolderOverviewResult {
  const blocks = extractBlocks(content)
  return {
    snippet: openingOf(blocks),
    endSnippet: endingOf(blocks),
    wordCount: countWordsFromContent(content),
  }
}

// A page never opened on this device has no saved file, only its encrypted
// copy (or nothing local at all until it is fetched). Those are read the way
// corpus-wide replace reads them, a few at a time, since each may reach the
// cloud.
const MAX_CRDT_READS = 4
let crdtReads = 0
const crdtQueue: Array<() => void> = []

async function readFromCrdt(pageId: string): Promise<string | null> {
  if (crdtReads >= MAX_CRDT_READS) await new Promise<void>((resolve) => crdtQueue.push(resolve))
  crdtReads++
  try {
    // Loaded on demand: corpusReplace imports this module to invalidate it.
    const { loadPage, releaseProvider } = await import('../services/corpus/corpusReplace')
    const loaded = await loadPage(pageId)
    if (loaded.kind !== 'crdt') return null
    const json = yXmlFragmentToProsemirrorJSON(loaded.ydoc.getXmlFragment('default'))
    void releaseProvider(pageId, loaded.provider)
    return JSON.stringify(json)
  } catch (err) {
    console.warn('[useFolderOverview] Could not read', pageId, err)
    return null
  } finally {
    crdtReads--
    crdtQueue.shift()?.()
  }
}

/**
 * A page's saved content: its file (or, on the web, its local copy, fetched
 * from the cloud if this device doesn't have it yet), else its CRDT. Null when
 * neither could be read, which is not the same as a page with no text.
 */
async function readContent(pageId: string): Promise<string | null> {
  readingPages.set(pageId, (readingPages.get(pageId) ?? 0) + 1)
  try {
    return await readContentNow(pageId)
  } finally {
    const left = (readingPages.get(pageId) ?? 1) - 1
    if (left) readingPages.set(pageId, left)
    else readingPages.delete(pageId)
  }
}

// Pages being read now. Reading a page this device doesn't have fetches it,
// which announces a change; views ignore that, or each read would start another.
const readingPages = new Map<string, number>()

async function readContentNow(pageId: string): Promise<string | null> {
  let content: string | null = null
  const api = typeof window !== 'undefined' ? (window as any).api : null
  if (api?.readPage) {
    const res = await api.readPage(pageId)
    if (res && res.success && res.content !== undefined) content = res.content
  }
  // A missing or textless file may still be behind the page's CRDT.
  if (content === null || !extractBlocks(content).length) {
    content = (await readFromCrdt(pageId)) ?? content
  }
  return content
}

// Pages whose text couldn't be read (sync not ready yet, or the cloud too
// slow). Nothing is cached for them; they are read again when sync becomes
// ready, and when catch-up brings them in (it calls invalidateFolderOverview).
const unreadPages = new Set<string>()
let watchingSync = false

function markUnread(pageId: string): void {
  unreadPages.add(pageId)
  if (watchingSync) return
  watchingSync = true
  // Loaded on demand, like corpusReplace above, to keep this module light.
  void import('../services/sync/syncEngine').then(({ syncEngine }) => {
    watch(() => syncEngine.canSync.value, (ready) => {
      if (!ready) return
      for (const pageId of [...unreadPages]) invalidateFolderOverview(pageId)
    })
  })
}

/**
 * Loads a short overview (opening, closing lines, word count) for a page,
 * using the same local-first content bridge the editor uses. Results are
 * cached briefly so reopening the same folder within a session doesn't
 * refetch every card. A page that couldn't be read comes back empty and
 * isn't cached; views hear when to try again through usePageTextChanges.
 */
export async function getFolderOverview(pageId: string): Promise<FolderOverviewResult> {
  const cached = overviewCache.get(pageId)
  if (cached && Date.now() - cached.fetchedAt < OVERVIEW_CACHE_TTL_MS) {
    return cached.result
  }

  const pending = inFlight.get(pageId)
  if (pending) return pending

  const fetchPromise = (async (): Promise<FolderOverviewResult> => {
    let result: FolderOverviewResult = { snippet: '', endSnippet: '', wordCount: 0 }
    try {
      const content = await readContent(pageId)
      if (content === null) {
        markUnread(pageId)
      } else {
        result = overviewOf(content)
        unreadPages.delete(pageId)
        overviewCache.set(pageId, { result, fetchedAt: Date.now() })
      }
    } catch (err) {
      console.warn('[useFolderOverview] Failed to load overview for', pageId, err)
      markUnread(pageId)
    } finally {
      inFlight.delete(pageId)
    }
    return result
  })()

  inFlight.set(pageId, fetchPromise)
  return fetchPromise
}

/** The most paragraphs a page's text is read to, for a preview. */
const MAX_PREVIEW_BLOCKS = 400

/**
 * A page's text, paragraph by paragraph, for reading it without opening it
 * (the explorers' details). Not cached: it is read when a page is picked.
 */
export async function getPageText(pageId: string): Promise<string[]> {
  try {
    const content = await readContent(pageId)
    if (content === null) {
      markUnread(pageId)
      return []
    }
    unreadPages.delete(pageId)
    return extractBlocks(content).slice(0, MAX_PREVIEW_BLOCKS)
  } catch (err) {
    console.warn('[useFolderOverview] Failed to read text for', pageId, err)
    markUnread(pageId)
    return []
  }
}

const invalidationListeners = new Set<(pageId: string) => void>()

/** Runs `listener` whenever a page's saved content changes. Returns an unsubscribe. */
export function onFolderOverviewInvalidated(listener: (pageId: string) => void): () => void {
  invalidationListeners.add(listener)
  return () => invalidationListeners.delete(listener)
}

export function invalidateFolderOverview(pageId: string): void {
  overviewCache.delete(pageId)
  for (const listener of invalidationListeners) listener(pageId)
}

/**
 * Calls `reread` while the component is mounted whenever the text of the page
 * `pageId()` names changes: it was edited, caught up from the cloud, or could
 * not be read before and can be now.
 */
export function usePageTextChanges(pageId: () => string | null | undefined, reread: () => void): void {
  const stop = onFolderOverviewInvalidated((changed) => {
    // A change announced while the page is being read is in what that read returns.
    if (changed === pageId() && !readingPages.has(changed)) reread()
  })
  if (getCurrentInstance()) onBeforeUnmount(stop)
}
